import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Laptop, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const CodeForm = ({ onSubmit, label, testid }) => {
  const [code, setCode] = useState("");
  return (
    <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}>
      <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={8} required className="rounded-full font-mono tracking-widest" data-testid={`${testid}-input`} />
      <Button type="submit" className="rounded-full" data-testid={`${testid}-submit`}>{label}</Button>
    </form>
  );
};

const MfaSection = () => {
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const call = async (fn, msg) => {
    try { const { data } = await fn(); setUser(data); setSetup(null); toast.success(msg); } catch (e) { toast.error(formatApiError(e)); }
  };
  const start = async () => {
    try { const { data } = await api.post("/me/mfa/setup"); setSetup(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (user.is_demo) {
    return <p className="text-sm text-muted-foreground" data-testid="mfa-demo-disabled">La double authentification n'est pas disponible sur les comptes de démonstration partagés.</p>;
  }
  if (user.mfa_enabled) {
    return (
      <div className="space-y-3" data-testid="mfa-enabled">
        <p className="flex items-center gap-2 text-sm font-medium text-success"><ShieldCheck className="h-4 w-4" />Double authentification active</p>
        <p className="text-xs text-muted-foreground">Pour la désactiver, saisissez un code de votre application.</p>
        <CodeForm label="Désactiver" testid="mfa-disable" onSubmit={(code) => call(() => api.post("/me/mfa/disable", { code }), "Double authentification désactivée")} />
      </div>
    );
  }
  if (!setup) {
    return (
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-sm text-muted-foreground"><ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />Protégez votre compte avec un code temporaire (Google Authenticator, 1Password…).</p>
        <Button variant="outline" className="rounded-full" onClick={start} data-testid="mfa-setup-button">Activer la double authentification</Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start" data-testid="mfa-setup-panel">
      <div className="rounded-2xl bg-white p-3"><QRCodeSVG value={setup.otpauth_uri} size={148} /></div>
      <div className="flex-1 space-y-3 text-sm">
        <p>1. Scannez le QR code avec votre application.</p>
        <p className="break-all text-xs text-muted-foreground">Ou saisissez la clé : <span className="num" data-testid="mfa-secret">{setup.secret}</span></p>
        <p>2. Entrez le code affiché :</p>
        <CodeForm label="Confirmer" testid="mfa-enable" onSubmit={(code) => call(() => api.post("/me/mfa/enable", { code }), "Double authentification activée")} />
      </div>
    </div>
  );
};

const device = (ua = "") => {
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /curl/.test(ua) ? "curl" : "Navigateur";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
};

const SessionsSection = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sessions"], queryFn: () => api.get("/me/sessions").then((r) => r.data) });
  const act = async (fn) => {
    try { await fn(); qc.invalidateQueries({ queryKey: ["sessions"] }); toast.success("Session déconnectée"); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="sessions-section">
      <ul className="divide-y divide-border">
        {(data || []).map((s) => (
          <li key={s.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`session-row-${s.id}`}>
            <Laptop className="h-4 w-4 shrink-0 text-sage" />
            <div className="min-w-0 flex-1">
              <div className="truncate">{device(s.user_agent)}</div>
              <div className="text-xs text-muted-foreground">Dernière activité : {new Date(s.last_used_at).toLocaleString("fr-FR")}</div>
            </div>
            {s.current ? <span className="rounded-full bg-mint px-2.5 py-0.5 text-[11px] font-semibold text-navy">Cet appareil</span>
              : !user.is_demo && <Button variant="ghost" size="sm" className="rounded-full" onClick={() => act(() => api.delete(`/me/sessions/${s.id}`))} data-testid={`session-revoke-${s.id}`}>Déconnecter</Button>}
          </li>
        ))}
      </ul>
      {data?.length > 1 && !user.is_demo && <Button variant="outline" size="sm" className="rounded-full" onClick={() => act(() => api.post("/me/sessions/revoke-others"))} data-testid="sessions-revoke-others">Déconnecter les autres appareils</Button>}
    </div>
  );
};

export const SecurityCard = () => (
  <section className="card-surface space-y-6 p-5 sm:p-6" data-testid="settings-security-card">
    <h2 className="text-lg font-bold">Sécurité</h2>
    <MfaSection />
    <div className="border-t border-border pt-5">
      <h3 className="mb-2 text-sm font-semibold">Sessions ouvertes</h3>
      <SessionsSection />
    </div>
  </section>
);
